import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../Firebase";
import { V3Project, V3User, V3UserSession } from "../types/v3types";
import { useAuth } from "../hooks/useAuth";
import styled from "styled-components";

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  padding: 40px;
  width: 100%;
  max-width: 400px;
`;

const ProjectTitle = styled.h1`
  font-size: 28px;
  margin-bottom: 10px;
  text-align: center;
  color: #333;
`;

const ProjectDescription = styled.p`
  font-size: 16px;
  color: #666;
  text-align: center;
  margin-bottom: 30px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #555;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Button = styled.button`
  padding: 14px 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  margin-top: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: #ffe3e3;
  color: #c92a2a;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
`;

const LoadingMessage = styled.div`
  text-align: center;
  font-size: 18px;
  color: white;
`;

const V3PlayerLogin: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user: firebaseUser, loading: authLoading } = useAuth();
  const [project, setProject] = useState<V3Project | null>(null);
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) {
      navigate("/");
      return;
    }

    if (authLoading) return;

    const loadProject = async () => {
      try {
        const projectDoc = await getDoc(doc(db, "v3projects", projectId));
        if (projectDoc.exists()) {
          const projectData = projectDoc.data() as V3Project;
          if (!projectData.isActive) {
            setError("このプロジェクトは現在利用できません");
          } else {
            setProject(projectData);
          }
        } else {
          setError("プロジェクトが見つかりません");
        }
      } catch (err) {
        setError("プロジェクトの読み込みに失敗しました");
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId, navigate, authLoading]);

  const generateHash = () => {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project || !projectId) return;
    
    setError("");
    setSubmitting(true);

    try {
      if (!firebaseUser) {
        setError("認証エラーが発生しました");
        return;
      }

      // Check if user already exists with this Firebase UID
      const usersQuery = query(
        collection(db, "v3users"),
        where("projectId", "==", projectId),
        where("firebaseUid", "==", firebaseUser.uid)
      );
      
      const userSnapshots = await getDocs(usersQuery);
      
      let userId: string;
      let userData: V3User;
      
      if (!userSnapshots.empty) {
        // Existing user
        const existingUser = userSnapshots.docs[0];
        userId = existingUser.id;
        userData = existingUser.data() as V3User;
        
        // Update last active
        await setDoc(doc(db, "v3users", userId), {
          ...userData,
          lastActiveAt: serverTimestamp()
        });
      } else {
        // New user
        userId = firebaseUser.uid + "_" + projectId;
        
        const newUserData: Omit<V3User, "createdAt" | "lastActiveAt"> = {
          userId,
          projectId,
          username,
          firebaseUid: firebaseUser.uid,
          isActive: true,
          role: "player",
          stats: {
            eventsCreated: 0,
            eventsParticipated: 0,
            totalScore: 0
          }
        };
        
        await setDoc(doc(db, "v3users", userId), {
          ...newUserData,
          createdAt: serverTimestamp(),
          lastActiveAt: serverTimestamp()
        });
        
        userData = newUserData as V3User;
      }
      
      // Create user session
      const sessionHash = generateHash();
      const sessionId = `${userId}_${Date.now()}`;
      
      const sessionData: Omit<V3UserSession, "createdAt" | "lastActiveAt"> = {
        sessionId,
        projectId,
        userId,
        hash: sessionHash,
        isActive: true,
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform
        }
      };
      
      await setDoc(doc(db, "v3sessions", sessionId), {
        ...sessionData,
        createdAt: serverTimestamp(),
        lastActiveAt: serverTimestamp()
      });
      
      // Store session in localStorage
      localStorage.setItem(`v3_session_${projectId}`, JSON.stringify({
        userId,
        username,
        hash: sessionHash,
        sessionId
      }));
      
      // Navigate to mypage
      navigate(`/v3/${projectId}/mypage`);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : "ログインに失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <Container>
        <LoadingMessage>読み込み中...</LoadingMessage>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container>
        <LoginCard>
          <ErrorMessage>{error || "プロジェクトが見つかりません"}</ErrorMessage>
        </LoginCard>
      </Container>
    );
  }

  return (
    <Container>
      <LoginCard>
        <ProjectTitle>{project.projectName}</ProjectTitle>
        {project.description && (
          <ProjectDescription>{project.description}</ProjectDescription>
        )}
        
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="username">ユーザー名</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="表示名を入力"
              required
              disabled={submitting}
              autoComplete="username"
            />
          </InputGroup>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <Button type="submit" disabled={submitting}>
            {submitting ? "ログイン中..." : "参加する"}
          </Button>
        </Form>
      </LoginCard>
    </Container>
  );
};

export default V3PlayerLogin;